import { useState, useEffect } from 'react';
import { getPerformanceAnalytics } from '../../services/performanceService';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

const PerformanceTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getPerformanceAnalytics(30);
        setPerformanceData(data);
      } catch (error) {
        console.error('Error loading performance data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading performance metrics...</p>
      </div>
    );
  }

  if (!performanceData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No performance data available</p>
      </div>
    );
  }

  // Error rates by component
  const errorByComponentData = {
    labels: Array.from(performanceData.metrics.errorRateByComponent.keys()) as string[],
    datasets: [
      {
        label: 'Errors',
        data: Array.from(performanceData.metrics.errorRateByComponent.values()) as number[],
        backgroundColor: '#ef4444',
      },
    ],
  };

  // Error trends
  const errorTrendsData = {
    labels: performanceData.errorTrends.map((t: any) => t.date),
    datasets: [
      {
        label: 'Errors',
        data: performanceData.errorTrends.map((t: any) => t.errors),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
      },
    ],
  };

  // Cost breakdown
  const costData = {
    labels: Array.from(performanceData.metrics.costPerOperation.keys()) as string[],
    datasets: [
      {
        label: 'Cost ($)',
        data: Array.from(performanceData.metrics.costPerOperation.values()) as number[],
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b'],
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Performance & Technical Metrics
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Crash Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {performanceData.metrics.crashRate.toFixed(2)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Firestore Reads</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {performanceData.metrics.firestoreReads.toLocaleString()}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Firestore Writes</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {performanceData.metrics.firestoreWrites.toLocaleString()}
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Errors by Component</h3>
          <BarChart data={errorByComponentData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Error Trends</h3>
          <LineChart data={errorTrendsData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Cost Breakdown by Operation</h3>
          <BarChart data={costData} height={300} />
        </div>
      </div>
    </div>
  );
};

export default PerformanceTab;

